/**
 * persistence/config.js
 *
 * Central configuration for the offline-first persistence system.
 * All tunable constants live here — change behaviour without touching logic files.
 *
 * Naming convention:
 *   *_MS     → milliseconds
 *   *_COUNT  → integer thresholds
 *   *_MAX    → upper bounds for pruning / retention
 */

// ─── IndexedDB ────────────────────────────────────────────────────────────────

/** Name of the Dexie database in the browser. */
export const DB_NAME = "stepout_analyst_portal";

/** Current schema version. Increment when schema changes. */
export const DB_VERSION = 3;

/**
 * Maximum number of local reviewer-portal snapshots retained per
 * (matchId, reviewerId). Oldest are pruned first. Local-only crash/undo
 * history for the review session — never synced to the cloud.
 */
export const REVIEWER_VERSIONS_MAX_PER_SESSION = 20;

// ─── Local write debounce ────────────────────────────────────────────────────

/**
 * How long to wait after the last Redux action before writing the session
 * record to IndexedDB.  200ms gives near-instant persistence without flooding
 * the IDB transaction queue.
 */
export const SESSION_WRITE_DEBOUNCE_MS = 200;

// ─── REST API sync ───────────────────────────────────────────────────────────

/**
 * Seconds of inactivity (no new SET_FULL_ANALYSE_STRING actions) before we
 * consider the analyst to have "paused typing" and flush the REST API sync queue.
 */
export const API_SYNC_TYPING_PAUSE_MS = 5_000; // 5 seconds

/**
 * Periodic background sync interval even if typing hasn't paused.
 * Guarantees data reaches the server at least this often during active sessions.
 */
export const API_SYNC_PERIODIC_MS = 2 * 60_000; // 2 minutes

/**
 * Maximum number of retry attempts for a failed REST API push before
 * the queue item is marked 'failed' and a persistent error toast shown.
 */
export const API_SYNC_MAX_RETRIES = 5;

/**
 * Base delay for exponential back-off on API retry.
 * Delay = BASE * 2^attempt  →  1s, 2s, 4s, 8s, 16s
 */
export const API_SYNC_RETRY_BASE_MS = 1_000;

/**
 * A queued save older than this (or left over from an earlier page load) is
 * compared with the server copy before it is sent, so an old unsent string on
 * one device can't overwrite newer work saved from another device.
 */
export const API_SYNC_VERIFY_AFTER_MS = 5 * 60_000;

// ─── Checkpoint (version snapshot) ───────────────────────────────────────────

/**
 * Minimum elapsed time since the last checkpoint before a new one may be
 * created by the time-based trigger.
 */
export const CHECKPOINT_INTERVAL_MS = 5 * 60_000; // 5 minutes

/**
 * Number of new events that must be added since the last checkpoint before
 * the event-count trigger fires.
 */
export const CHECKPOINT_EVENT_DELTA = 10;

/**
 * Maximum number of version checkpoints retained per (matchId, analystId).
 * Older records are pruned on session start.
 * 20 checkpoints × 5-min intervals = 100 minutes of rolling local history.
 * Kept in sync with FIRESTORE_VERSIONS_CLOUD_MAX so a full-sync produces
 * an identical set in both stores.
 */
export const VERSIONS_MAX_PER_SESSION = 20;

/**
 * Maximum number of diffLog entries retained per (matchId, analystId).
 * At ~1 diff per 200ms during active tagging, 1000 entries ≈ ~3 min of
 * dense tagging history, comfortably covering any crash window.
 */
export const DIFF_LOG_MAX_PER_SESSION = 1_000;

// ─── Sync queue retention ─────────────────────────────────────────────────────

/**
 * Age in milliseconds after which completed (status='done') sync queue
 * entries are pruned.  24 hours keeps today's session, discards yesterday's.
 */
export const SYNC_QUEUE_DONE_MAX_AGE_MS = 24 * 60 * 60_000;

/**
 * Age in milliseconds after which a session and ALL its associated records
 * (versions, diffLog, firestoreQueue items) are deleted by the global stale
 * data cleanup.  30 days gives analysts a full month of local history before
 * the oldest work is discarded.  The server always holds the authoritative
 * copy — this is only local crash-recovery data.
 *
 * Reduce to 14 * 24 * 60 * 60_000 if storage pressure is observed.
 */
export const STALE_SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60_000; // 30 days

/**
 * Age in milliseconds after which completed firestoreQueue items are deleted.
 * Shorter than STALE_SESSION_MAX_AGE_MS because done items are useless once
 * flushed — they only need to survive long enough to confirm the write succeeded.
 * This also limits how long full event strings sit in the firestoreQueue payload
 * field after the Firestore write has already succeeded.
 */
export const FIRESTOREQUEUE_DONE_MAX_AGE_MS = 7 * 24 * 60 * 60_000; // 7 days

// ─── Recovery ─────────────────────────────────────────────────────────────────

/**
 * If the local session record is newer than the last API sync by at least
 * this many milliseconds, offer crash recovery to the analyst.
 * 30 seconds prevents spurious recovery prompts caused by race conditions
 * between the IndexedDB write and the API push.
 */
export const RECOVERY_THRESHOLD_MS = 30_000;

// ─── Firestore ────────────────────────────────────────────────────────────────

/**
 * Firestore top-level collection that contains all analyst sessions.
 */
export const FIRESTORE_SESSIONS_COLLECTION = "analystSessions";

/**
 * How often the live current-string backup is flushed to Firestore.
 * The session-backup queue entry is upserted on every REST API success, so
 * N syncs within one flush window collapse into a single Firestore setDoc.
 * 30 min keeps the cloud copy reasonably fresh while capping writes to
 * ~2/hr per analyst (vs 30/hr with an immediate flush strategy).
 */
export const FIRESTORE_BACKUP_PERIODIC_MS = 30 * 60_000; // 30 minutes

/**
 * Minimum elapsed time between Firestore checkpoint writes triggered by the
 * automatic time / event-count triggers.  IndexedDB captures a full snapshot
 * every 5 minutes locally; Firestore only needs coarse cloud snapshots.
 */
export const FIRESTORE_CHECKPOINT_MIN_INTERVAL_MS = 90 * 60_000; // 90 minutes

/**
 * Minimum elapsed time between Firestore checkpoint writes triggered by a
 * successful REST API sync ("api-success").  Shorter than the auto interval
 * so managers see reasonably up-to-date cloud history after an explicit save,
 * while still batching rapid sync cycles into a single Firestore write.
 *
 * Budget: 3 writes/hr/analyst × 22 analysts × 12h = 792 writes/day
 */
export const FIRESTORE_API_SUCCESS_MIN_INTERVAL_MS = 20 * 60_000; // 20 minutes

/**
 * Maximum number of version checkpoints retained per session in Firestore.
 * Must equal VERSIONS_MAX_PER_SESSION so that a manual full-sync (Sync to Cloud)
 * produces an identical set in both stores.
 * Oldest entries are pruned automatically after each new checkpoint write, and
 * the full-sync path replaces the entire collection in one atomic batch.
 */
export const FIRESTORE_VERSIONS_CLOUD_MAX = 20;

/**
 * Placeholder/reminder: set this in .env as REACT_APP_FIREBASE_*
 * The actual values are read from the environment at runtime.
 */
export const FIREBASE_CONFIG_KEYS = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
];

// ─── Version control (Yjs git-style layer) ─────────────────────────────────────

/**
 * Name of the default branch automatically created for each analyst on a match.
 * `{analystId}` is interpolated at runtime → e.g. "me/123".  Mirrors git's
 * "main" but namespaced per analyst since each analyst owns one working copy.
 */
export const VCS_DEFAULT_BRANCH_PREFIX = "me";

/**
 * Maximum number of commits retained per branch in IndexedDB.
 * Older commits are pruned oldest-first (the branch HEAD state is always kept
 * separately in the branches table, so pruning never loses current work).
 * Kept at 20 to match the cloud cap (VCS_CLOUD_COMMITS_MAX) so local and cloud
 * histories stay aligned — same retention the legacy checkpoint system used.
 */
export const VCS_BRANCH_COMMITS_MAX = 20;

/**
 * Maximum number of commits retained per branch in Firestore.
 * Oldest cloud commits are pruned after each push to stay within the free-tier
 * write budget.  Must equal VCS_BRANCH_COMMITS_MAX so local and cloud agree.
 */
export const VCS_CLOUD_COMMITS_MAX = 20;

/**
 * Debounce before reconciling a live Redux string into the active branch's
 * Y.Doc.  Slightly longer than the IDB session write so we batch keystrokes
 * into fewer Yjs transactions without lagging behind the editor.
 */
export const VCS_BRIDGE_DEBOUNCE_MS = 400;

/**
 * Firestore subcollection names for pushed branches and commits, nested under
 * the existing `/analystSessions/{analystId}_{matchId}` session document.
 */
export const FIRESTORE_BRANCHES_SUBCOLLECTION = "branches";
export const FIRESTORE_COMMITS_SUBCOLLECTION  = "commits";

/**
 * Firestore subcollection for reviewer-portal version snapshots, nested under
 * the reviewer's session document `/analystSessions/{reviewerId}_{matchId}`.
 */
export const FIRESTORE_REVIEWER_VERSIONS_SUBCOLLECTION = "reviewerVersions";

/**
 * Maximum reviewer snapshots retained in Firestore per (matchId, reviewerId).
 * Oldest are pruned after each push to stay within the write budget.
 */
export const REVIEWER_VERSIONS_CLOUD_MAX = 20;

// ─── Sentinel event string ────────────────────────────────────────────────────

/**
 * The default/empty event token used throughout the app.
 * Filtered out when counting meaningful events.
 */
export const EMPTY_EVENT_TOKEN = "X-X-X-X-X-X-X-X-X-X-X-X";

/**
 * Tab-lock localStorage key — must match the key used in App.js.
 */
export const TAB_LOCK_KEY = "stepout_active_tab";

// ─── Cross-match string guard ─────────────────────────────────────────────────

/**
 * Below this many events the current string is too small to judge whether a
 * replacement is foreign, so only the copy-record and squad checks apply.
 */
export const STRING_GUARD_MIN_EVENTS = 20;

/**
 * Share of current events (matched by video time + jersey) missing from the
 * incoming string at which a replacement is treated as a bulk/foreign
 * replace and must be confirmed.
 */
export const STRING_GUARD_MISSING_SHARE = 0.3;

/** Event-count change (relative) that is flagged on paste. */
export const STRING_GUARD_COUNT_CHANGE_SHARE = 0.3;

/** Share of pasted events whose jersey is not in either squad that is flagged. */
export const STRING_GUARD_UNKNOWN_JERSEY_SHARE = 0.2;

/** localStorage key holding a fingerprint of the last string copied from Version History. */
export const COPIED_STRING_KEY = "stepout_last_copied_string";

/** localStorage key holding this browser's stable device id (survives logout). */
export const DEVICE_ID_KEY = "stepout_device_id";
